import { useState, useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LayoutDashboard, Newspaper, Settings, LogOut, Menu, X, Users, Sparkles } from 'lucide-react';
import api from '@/lib/api';

const LOGO_URL = "/coyp-logo.png";

const navItems = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/articles', label: 'Stories', icon: Newspaper },
  { to: '/admin/ai-tools', label: 'AI Tools', icon: Sparkles },
  { to: '/admin/subscribers', label: 'Subscribers', icon: Users },
  { to: '/admin/settings', label: 'Ad Settings', icon: Settings },
];

const AdminLayout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeJobs, setActiveJobs] = useState(0);

  // Poll for active AI jobs
  useEffect(() => {
    const check = async () => {
      try {
        const res = await api.get('/admin/ai/jobs');
        const active = (res.data || []).filter(j => !['done', 'failed'].includes(j.status)).length;
        setActiveJobs(active);
      } catch (e) {}
    };
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, []);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const openMenu = useCallback(() => setOpen(true), []);
  const closeMenu = useCallback(() => setOpen(false), []);

  return (
    <div className="min-h-screen bg-black">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#111] border-b border-[#222] flex items-center justify-between px-4 py-3">
        <button
          type="button"
          onClick={openMenu}
          className="text-white p-2 -m-2"
          data-testid="admin-menu-toggle"
        >
          <Menu size={24} />
        </button>
        <img src={LOGO_URL} alt="COYP TV" className="h-8 object-contain" />
        <div className="w-8" />
      </div>

      {/* Sidebar + Overlay wrapper */}
      <div
        className={`fixed inset-0 z-40 md:z-auto md:relative ${open ? '' : 'pointer-events-none md:pointer-events-auto'}`}
      >
        {/* Dark overlay - mobile only */}
        <div
          className={`absolute inset-0 bg-black/70 transition-opacity duration-200 md:hidden ${
            open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMenu}
          data-testid="sidebar-overlay"
        />

        {/* Sidebar */}
        <aside
          className={`absolute left-0 top-0 w-56 h-full bg-[#111] border-r border-[#222] z-10 transition-transform duration-200 pointer-events-auto
            ${open ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:fixed`}
          data-testid="admin-sidebar"
        >
          <div className="p-4 border-b border-[#222]">
            <div className="flex items-center justify-between">
              <img src={LOGO_URL} alt="COYP TV" className="h-10 object-contain" />
              <button
                type="button"
                onClick={closeMenu}
                className="md:hidden text-gray-400 hover:text-white p-1"
                data-testid="sidebar-close-btn"
              >
                <X size={20} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 mt-3 px-3 py-2 w-full text-gray-400 hover:text-yellow-500 transition-colors text-sm"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>

          <nav className="p-4 space-y-1">
            {navItems.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                onClick={closeMenu}
                className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                  isActive(to)
                    ? 'bg-[#222] text-white'
                    : 'text-gray-400 hover:bg-[#222] hover:text-white'
                }`}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{label}</span>
                {label === 'AI Tools' && activeJobs > 0 && (
                  <span className="ml-auto w-5 h-5 rounded-full bg-yellow-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                    {activeJobs}
                  </span>
                )}
              </Link>
            ))}
          </nav>

        </aside>
      </div>

      {/* Main Content */}
      <main className="md:ml-56 px-4 pb-4 sm:px-6 sm:pb-6 md:px-8 md:pb-8 pt-16 md:pt-8">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
